import { useEffect, useMemo, useRef, useState } from "react";
import withLoader from "../../../components/layouts/WithLoader";
import { ToastContainer, toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import WarehouseService from "../../../services/WarehouseService";
import getCurrentUserId from "../../../reduxSaga/sagas/requests/currentUserId";
import api from "../../../services/api";
import Select from "react-select";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 Mo

const styles = {
  container: (base) => ({
    ...base,
    flex: 1,
  }),
};

// Doit rester identique à l'ordre des colonnes lu par le backend (addProductsExcelWithHierarchy)
const TEMPLATE_HEADERS = [
  "Libellé",
  "Description (séparer les lignes par ' - ')",
  "Code article",
  "Code à barre (si non disponible mettre -)",
  "TVA",
  "Famille",
  "Sous famille",
  "Catégorie",
  "Marque",
  "Prix achat",
  "Prix vente",
  "Qte",
  "Libellé (anglais, facultatif)",
  "Description (anglais, facultatif, séparer les lignes par ' - ')",
];

const TEMPLATE_EXAMPLE = [
  "Produit exemple",
  "Première ligne - Deuxième ligne",
  "-",
  "-",
  20,
  "Nom de la famille",
  "Nom de la sous famille",
  "Nom de la catégorie",
  "Nom de la marque",
  10,
  15,
  5,
  "Example product",
  "First line - Second line",
];

const UploadExcelProductSellNewProducts = ({ startLoading, stopLoading }) => {
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [result, setResult] = useState(null);
  const uploadingRef = useRef(false);

  useEffect(() => {
    WarehouseService.getAllRealWarehouseForPrice(getCurrentUserId(), 0)
      .then((response) => {
        if (response.data.success) {
          setWarehouses(response.data.success);
        } else {
          toast.error("Erreur : pas de dépôt");
        }
      })
      .catch(() => {
        toast.error("Impossible de charger la liste des dépôts");
      });
  }, []);

  const warehouseOptions = useMemo(
    () =>
      warehouses
        .filter((w) => w?.id !== undefined)
        .map((w) => ({ value: w.id, label: w.name })),
    [warehouses]
  );

  const onTelechargerModel = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, TEMPLATE_EXAMPLE]);
    worksheet["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 24 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Model");
    const fileBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([fileBuffer], { type: "application/octet-stream" }),
      "model-nouveaux-produits.xlsx"
    );
  };

  const resetFile = () => {
    setFile(null);
    setFileInputKey((k) => k + 1);
  };

  const handleFileUpload = (e) => {
    const selected = e.target.files[0];
    setResult(null);
    if (!selected) {
      setFile(null);
      return;
    }
    if (!/\.xlsx$/i.test(selected.name)) {
      toast.error("Le fichier doit être au format .xlsx");
      resetFile();
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      toast.error("Le fichier dépasse 10 Mo");
      resetFile();
      return;
    }
    setFile(selected);
  };

  const onChangeWarehouse = (option) => {
    setSelectedWarehouse(option);
    // Un fichier choisi pour un autre dépôt ne doit pas être envoyé par erreur
    setResult(null);
    resetFile();
  };

  const onTelechargerRejets = () => {
    if (!result?.rejected?.length) return;
    const rows = [
      ["Ligne", "Libellé", "Motif"],
      ...result.rejected.map((r) => [r.line, r.name, r.reason]),
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), "Rejets");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), "produits-non-ajoutes.xlsx");
  };

  const handleAjouter = () => {
    if (uploadingRef.current) return;
    if (!selectedWarehouse) {
      toast.error("Veuillez choisir un dépôt");
      return;
    }
    if (!file) {
      toast.error("Veuillez choisir un fichier Excel");
      return;
    }

    uploadingRef.current = true;
    setIsUploading(true);
    setResult(null);
    startLoading();

    const formData = new FormData();
    formData.append("file", file);
    const url = `/retail/warehouse/addProductsExcelWithHierarchy/${getCurrentUserId()}/${selectedWarehouse.value}`;

    api
      .post(url, formData, { headers: { Accept: "application/json" } })
      .then((response) => {
        const data = response.data || {};
        if (data.status === "error") {
          toast.error(data.message || "Erreur lors de l'importation");
          return;
        }
        const rejected = data.rejected || [];
        setResult({ added: data.added || 0, existing: data.existing || [], rejected });
        if (rejected.length === 0) {
          toast.success(`${data.added || 0} produit(s) ajouté(s)`);
        } else {
          toast.warning(
            `${data.added || 0} produit(s) ajouté(s), ${rejected.length} ligne(s) rejetée(s)`
          );
        }
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message || "Erreur lors du téléchargement du fichier"
        );
      })
      .finally(() => {
        stopLoading();
        uploadingRef.current = false;
        setIsUploading(false);
      });
  };

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <ToastContainer />
      <section className="content-header">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-6"></div>
            <div className="col-md-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">Home</li>
                <li className="breadcrumb-item active">Ajout de nouveaux produits</li>
              </ol>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card card-dark">
                <div className="card-footer">
                  <h3>Ajouter de nouveaux produits</h3>
                </div>
                <div className="p-3 d-flex justify-content-between align-items-center">
                  <h4>Télécharger le Model Excel</h4>
                  <button className="btn btn-dark float-right" onClick={onTelechargerModel}>
                    <i className="fas fa-download"></i> Télécharger model Excel
                  </button>
                </div>
                <div className="card-body my-0 py-0">
                  <p>
                    Si la valeur de la colonne n'est pas présente merci de mettre -. La famille, la
                    sous famille, la catégorie et la marque doivent exister (saisir leur nom). Les
                    produits sont créés en achat, en vente, produit fini et matière première. Dans la
                    description, chaque " - " (tiret entouré d'espaces) crée un retour à la ligne.
                  </p>
                </div>
                <div className="card-body mt-0 pt-0">
                  <div className="row">
                    <div className="col-md-6 d-flex flex-column">
                      <div className="form-group">
                        <label htmlFor="warehouse-select">Choisir le dépôt</label>
                        <Select
                          inputId="warehouse-select"
                          styles={styles}
                          closeMenuOnSelect={true}
                          onChange={onChangeWarehouse}
                          options={warehouseOptions}
                          value={selectedWarehouse}
                          placeholder="Choisir..."
                        />
                      </div>
                    </div>

                    <div className="col-md-6 d-flex flex-column">
                      <div className="form-group">
                        <label htmlFor="products-file">Fichier Excel (.xlsx)</label>
                        <input
                          id="products-file"
                          key={fileInputKey}
                          style={{ border: "1px solid hsl(0, 0%, 80%)", width: "100%" }}
                          type="file"
                          accept=".xlsx"
                          className="btn float-right"
                          onChange={handleFileUpload}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  <div className="p-3 d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-dark float-end"
                      onClick={handleAjouter}
                      title="Ajouter cette liste"
                      aria-label="Ajouter cette liste"
                      disabled={isUploading || !selectedWarehouse || !file}
                    >
                      <i className="fas fa-location-arrow" aria-hidden="true"></i> Ajouter cette liste
                    </button>
                  </div>
                </div>
              </div>

              {result && (
                <div className="card" aria-live="polite">
                  <div className="card-body">
                    <h4>Résultat de l'importation</h4>
                    <p>{result.added} produit(s) ajouté(s).</p>

                    {result.existing.length > 0 && (
                      <>
                        <h5>Produits déjà existants (code à barre) : prix et stock mis à jour</h5>
                        <p>{result.existing.join(", ")}</p>
                      </>
                    )}

                    {result.rejected.length > 0 && (
                      <>
                        <div className="d-flex justify-content-between align-items-center">
                          <h5>Lignes non ajoutées ({result.rejected.length})</h5>
                          <button className="btn btn-sm btn-dark" onClick={onTelechargerRejets}>
                            <i className="fas fa-download"></i> Télécharger la liste
                          </button>
                        </div>
                        <table className="table table-sm table-bordered">
                          <thead>
                            <tr>
                              <th>Ligne</th>
                              <th>Libellé</th>
                              <th>Motif</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.rejected.map((r) => (
                              <tr key={`${r.line}-${r.name}`}>
                                <td>{r.line}</td>
                                <td>{r.name}</td>
                                <td>{r.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default withLoader(UploadExcelProductSellNewProducts);
